import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Users, CalendarCheck, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export default function AdminDashboard() {
    const [leads, setLeads] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [l, b] = await Promise.all([
                axios.get(`${API}/leads`, { withCredentials: true }),
                axios.get(`${API}/bookings`, { withCredentials: true }),
            ]);
            setLeads(l.data);
            setBookings(b.data);
        } catch (err) {
            if (err.response?.status === 401) {
                try {
                    await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
                    const [l, b] = await Promise.all([
                        axios.get(`${API}/leads`, { withCredentials: true }),
                        axios.get(`${API}/bookings`, { withCredentials: true }),
                    ]);
                    setLeads(l.data);
                    setBookings(b.data);
                } catch {
                    navigate("/admin/login");
                }
            }
        }
        setLoading(false);
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        navigate("/admin/login");
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#081226] text-[#F0EBE0] px-6 py-10">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <span className="flex items-center gap-2.5">
                            <img src="/assets/logo_mark.png" alt="Ink of Destiny" className="h-11 w-auto" />
                            <span className="font-display italic text-lg text-[#F0EBE0]">Ink of <span className="text-[#C9A962]">Destiny</span></span>
                        </span>
                        <div>
                            <h1 className="font-display text-2xl font-semibold">Leads Dashboard</h1>
                            <p className="text-xs text-[#5C5A56] uppercase tracking-[0.2em]">Private — Maitrii Shahh</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button data-testid="admin-refresh-btn" onClick={load}
                            className="px-5 py-2.5 rounded-full border border-white/10 text-xs uppercase tracking-[0.15em] text-[#A19D94] hover:border-[#C9A962]/50 hover:text-[#C9A962] transition-colors flex items-center gap-2">
                            <RefreshCw size={14} /> Refresh
                        </button>
                        <button data-testid="admin-logout-btn" onClick={logout}
                            className="px-5 py-2.5 rounded-full border border-white/10 text-xs uppercase tracking-[0.15em] text-[#A19D94] hover:border-red-400/50 hover:text-red-400 transition-colors flex items-center gap-2">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {[
                        { icon: CalendarCheck, label: "Booking Requests", value: bookings.length, testid: "stat-bookings" },
                        { icon: Sparkles, label: "Mini Reading Leads", value: leads.length, testid: "stat-leads" },
                        { icon: Users, label: "Total Enquiries", value: bookings.length + leads.length, testid: "stat-total" },
                    ].map(({ icon: Icon, label, value, testid }) => (
                        <div key={testid} data-testid={testid} className="rounded-2xl border border-white/10 bg-[#0E1B33] p-6 flex items-center gap-5">
                            <span className="w-12 h-12 rounded-full border border-[#C9A962]/40 flex items-center justify-center text-[#C9A962]">
                                <Icon size={20} />
                            </span>
                            <div>
                                <p className="font-mono-num text-3xl text-[#C9A962]">{value}</p>
                                <p className="text-xs uppercase tracking-[0.2em] text-[#A19D94] mt-1">{label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {loading ? (
                    <p data-testid="admin-loading" className="mt-16 text-center text-[#5C5A56] text-sm">Loading…</p>
                ) : (
                    <>
                        <h2 className="mt-12 mb-4 font-display text-xl font-semibold text-[#C9A962]">Booking Requests</h2>
                        <div className="rounded-2xl border border-white/10 bg-[#0E1B33] overflow-x-auto">
                            <table data-testid="bookings-table" className="w-full text-sm min-w-[760px]">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-[#5C5A56] border-b border-white/5">
                                        <th className="px-6 py-4">Date</th><th className="px-6 py-4">Name</th><th className="px-6 py-4">Phone</th>
                                        <th className="px-6 py-4">Service</th><th className="px-6 py-4">Mode</th><th className="px-6 py-4">Message</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {bookings.length === 0 && (
                                        <tr><td colSpan={6} className="px-6 py-8 text-center text-[#5C5A56]">No bookings yet.</td></tr>
                                    )}
                                    {bookings.map((b) => (
                                        <tr key={b.id} className="hover:bg-white/[0.02]">
                                            <td className="px-6 py-4 text-[#5C5A56] whitespace-nowrap">{fmtDate(b.created_at)}</td>
                                            <td className="px-6 py-4 text-[#F0EBE0]">{b.name}</td>
                                            <td className="px-6 py-4"><a href={`tel:${b.phone}`} className="text-[#C9A962] hover:underline">{b.phone}</a></td>
                                            <td className="px-6 py-4 text-[#A19D94]">{b.service}</td>
                                            <td className="px-6 py-4"><span className="px-3 py-1 rounded-full border border-[#C9A962]/30 text-[#C9A962] text-xs">{b.mode}</span></td>
                                            <td className="px-6 py-4 text-[#5C5A56] max-w-[220px] truncate">{b.message || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h2 className="mt-12 mb-4 font-display text-xl font-semibold text-[#C9A962]">Mini Reading Leads</h2>
                        <div className="rounded-2xl border border-white/10 bg-[#0E1B33] overflow-x-auto">
                            <table data-testid="leads-table" className="w-full text-sm min-w-[640px]">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-[#5C5A56] border-b border-white/5">
                                        <th className="px-6 py-4">Date</th><th className="px-6 py-4">Name</th><th className="px-6 py-4">Birth Date</th>
                                        <th className="px-6 py-4">Life Path</th><th className="px-6 py-4">Contact</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {leads.length === 0 && (
                                        <tr><td colSpan={5} className="px-6 py-8 text-center text-[#5C5A56]">No leads yet.</td></tr>
                                    )}
                                    {leads.map((l) => (
                                        <tr key={l.id} className="hover:bg-white/[0.02]">
                                            <td className="px-6 py-4 text-[#5C5A56] whitespace-nowrap">{fmtDate(l.created_at)}</td>
                                            <td className="px-6 py-4 text-[#F0EBE0]">{l.name}</td>
                                            <td className="px-6 py-4 text-[#A19D94]">{l.dob}</td>
                                            <td className="px-6 py-4"><span className="font-mono-num text-lg text-[#C9A962]">{l.life_path}</span></td>
                                            <td className="px-6 py-4 text-[#C9A962]">{l.contact}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
